import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, ArrowLeft, Palette, Shield, MessageSquare, FileText, Upload, X, Plus, Link, File, GripVertical, Eye, EyeOff, Trash2, List, CreditCard, Building2, Banknote, QrCode, Search, Wallet, User, CheckCircle2, XCircle, Loader2, Settings2, Key, Webhook, Copy, ExternalLink, Edit2, Globe, Code2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { companyService, api, apiKeyService, webhookService, bankAccountService, descargoService, portfolioService } from '@/lib/api';
import { QRCodeSVG } from 'qrcode.react';
import { APP_VERSION } from '@/version';
import { SettingsAppearance } from '@/components/settings/SettingsAppearance';
import { SettingsPayments } from '@/components/settings/SettingsPayments';
import { SettingsApiWebhooks } from '@/components/settings/SettingsApiWebhooks';
import { SettingsCrm } from '@/components/settings/SettingsCrm';
import SettingsGestion from '@/components/settings/SettingsGestion';
import { ListChecks } from 'lucide-react';
import { SettingsDescargos } from '@/components/settings/SettingsDescargos';
import { SettingsWidget } from '@/components/settings/SettingsWidget';
import { SettingsEmail } from '@/components/settings/SettingsEmail';

const CompanySettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [activeSection, setActiveSection] = useState('appearance');
  const logoInputRef = useRef(null);
  
  // Mercado Pago state
  const [mpConnectionStatus, setMpConnectionStatus] = useState('unknown');
  const [showMPConfig, setShowMPConfig] = useState(false);
  const [mpCredentials, setMpCredentials] = useState({ access_token: '', public_key: '' });
  
  // CRM Moracero state
  const [crmConfig, setCrmConfig] = useState({ url: '', client_id: '', username: '', password: '' });
  const [crmTestStatus, setCrmTestStatus] = useState('idle'); // idle, testing, success, error
  const [crmTestDni, setCrmTestDni] = useState('');
  const [crmQueryResult, setCrmQueryResult] = useState(null);
  const [crmQuerying, setCrmQuerying] = useState(false);
  
  // API Keys & Webhooks state
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState({ events: [], descriptions: {} });
  const [showCreateApiKey, setShowCreateApiKey] = useState(false);
  const [showCreateWebhook, setShowCreateWebhook] = useState(false);
  const [newApiKey, setNewApiKey] = useState({ name: '', permissions: ['debtors:read', 'payments:read'], default_portfolio_id: '' });
  const [apiPortfolios, setApiPortfolios] = useState([]);
  const [editingApiKey, setEditingApiKey] = useState(null);
  const [newWebhook, setNewWebhook] = useState({ url: '', events: [] });
  const [createdApiKey, setCreatedApiKey] = useState(null);
  
  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showAddBankAccount, setShowAddBankAccount] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [newBankAccount, setNewBankAccount] = useState({
    bank: '',
    account_type: 'caja_ahorro',
    cbu: '',
    alias: '',
    holder: '',
    logo_url: '',
    is_default: false
  });

  // Descargo config state
  const [descargoConfig, setDescargoConfig] = useState({
    enabled: false,
    require_phone: false,
    require_email: false,
    allow_observation: true,
    allow_attachment: true,
    causes: []
  });
  const [newCause, setNewCause] = useState('');
  
  const [settings, setSettings] = useState({
    name: '',
    slug: '',
    logo_url: '',
    primary_color: '#0d9488',
    secondary_color: '#0f172a',
    search_by_dni: true,
    search_by_cuit: false,
    search_by_code: true,
    search_by_plate: false,
    require_phone_verification: false,
    show_online_users: false,
    enable_refinancing: false,
    refinancing_policy_urls: [],
    negotiation_fields: [],
    debtor_fields_config: { fields: [] },
    search_title: 'Consulta tu Deuda',
    search_subtitle: 'Ingresa tu DNI o Código de Cliente para ver tus deudas pendientes',
    search_icon: 'search',
    payment_methods: {
      mercadopago: true,
      transfer: false,
      modo: false,
      pagofacil: false,
      rapipago: false
    },
    transfer_config: {
      cbu: '',
      alias: '',
      bank: '',
      holder: ''
    },
    mercadopago_credentials: null,
    mp_webhook_mode: 'auto'
  });

  const [newPolicyUrl, setNewPolicyUrl] = useState('');
  const policyFileRef = useRef(null);
  
  const [showAddField, setShowAddField] = useState(false);
  const [newField, setNewField] = useState({
    field_id: '',
    field_name: '',
    field_type: 'text',
    is_visible_to_debtor: true,
    is_required: false,
    display_order: 99,
    options: []
  });
  const [newFieldOption, setNewFieldOption] = useState('');

  const FIELD_TYPES = [
    { value: 'text', label: 'Texto' },
    { value: 'number', label: 'Número' },
    { value: 'date', label: 'Fecha' },
    { value: 'select', label: 'Lista de Opciones' },
    { value: 'link', label: 'Enlace/URL' }
  ];

  const MENU_ITEMS = [
    { id: 'appearance', label: 'Apariencia', icon: Palette },
    { id: 'access', label: 'Acceso', icon: Shield },
    { id: 'payments', label: 'Medios de Pago', icon: CreditCard },
    { id: 'fields', label: 'Campos Cliente', icon: List },
    { id: 'negotiation', label: 'Negociación', icon: MessageSquare },
    { id: 'refinancing', label: 'Refinanciación', icon: FileText },
    { id: 'gestion', label: 'Gestión (CRM)', icon: ListChecks },
    { id: 'api', label: 'API & Webhooks', icon: Key },
    { id: 'crm', label: 'CRM Moracero', icon: Globe },
    { id: 'email', label: 'Email', icon: MessageSquare },
    { id: 'descargos', label: 'Descargos', icon: Shield },
    { id: 'widget', label: 'Widget Web', icon: Code2 },
  ];

  const API_PERMISSIONS = [
    { id: 'debtors:read', label: 'Leer clientes' },
    { id: 'debtors:write', label: 'Crear/actualizar clientes' },
    { id: 'actas:read', label: 'Consultar actas' },
    { id: 'actas:write', label: 'Crear actas + subir PDF/video' },
    { id: 'payments:read', label: 'Leer pagos' },
    { id: 'payments:write', label: 'Registrar pagos' },
    { id: 'portfolios:read', label: 'Leer carteras' },
    { id: 'requests:read', label: 'Leer solicitudes' },
    { id: 'requests:write', label: 'Crear solicitudes' },
    { id: 'webhooks:manage', label: 'Gestionar webhooks' },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (settings.mercadopago_credentials?.access_token) {
      checkMPConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.mercadopago_credentials]);

  useEffect(() => {
    if (activeSection === 'api') {
      loadApiKeys();
      loadWebhooks();
      loadWebhookEvents();
      portfolioService.list().then(r => setApiPortfolios(r.data || [])).catch(() => {});
    }
  }, [activeSection]);

  const loadApiKeys = async () => {
    try {
      const response = await apiKeyService.list();
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error loading API keys:', error);
    }
  };

  const loadWebhooks = async () => {
    try {
      const response = await webhookService.list();
      setWebhooks(response.data);
    } catch (error) {
      console.error('Error loading webhooks:', error);
    }
  };

  const loadWebhookEvents = async () => {
    try {
      const response = await webhookService.getEvents();
      setWebhookEvents(response.data);
    } catch (error) {
      console.error('Error loading webhook events:', error);
    }
  };

  const handleEditApiKey = (key) => {
    setEditingApiKey(key);
    setNewApiKey({ name: key.name, permissions: key.permissions || [], default_portfolio_id: key.default_portfolio_id || '', is_active: key.is_active });
    setShowCreateApiKey(true);
  };

  const handleCreateApiKey = async () => {
    if (!newApiKey.name.trim()) {
      toast.error('Ingresa un nombre para la API Key');
      return;
    }
    if (editingApiKey) {
      try {
        await apiKeyService.update(editingApiKey.id, {
          name: newApiKey.name,
          permissions: newApiKey.permissions,
          default_portfolio_id: newApiKey.default_portfolio_id || null,
          clear_default_portfolio: !newApiKey.default_portfolio_id,
          is_active: newApiKey.is_active,
        });
        setShowCreateApiKey(false);
        setEditingApiKey(null);
        setNewApiKey({ name: '', permissions: ['debtors:read', 'payments:read'], default_portfolio_id: '' });
        loadApiKeys();
        toast.success('API Key actualizada');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al actualizar API Key');
      }
      return;
    }
    try {
      const response = await apiKeyService.create({ ...newApiKey, default_portfolio_id: newApiKey.default_portfolio_id || null });
      setCreatedApiKey(response.data);
      setShowCreateApiKey(false);
      setNewApiKey({ name: '', permissions: ['debtors:read', 'payments:read'], default_portfolio_id: '' });
      loadApiKeys();
      toast.success('API Key creada exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear API Key');
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('¿Estás seguro de revocar esta API Key? Las integraciones que la usen dejarán de funcionar.')) return;
    try {
      await apiKeyService.revoke(keyId);
      loadApiKeys();
      toast.success('API Key revocada');
    } catch (error) {
      toast.error('Error al revocar API Key');
    }
  };

  const handleCreateWebhook = async () => {
    if (!newWebhook.url.trim()) {
      toast.error('Ingresa una URL para el webhook');
      return;
    }
    if (!newWebhook.url.startsWith('https://')) {
      toast.error('La URL debe usar HTTPS');
      return;
    }
    if (newWebhook.events.length === 0) {
      toast.error('Selecciona al menos un evento');
      return;
    }
    try {
      await webhookService.create(newWebhook);
      setShowCreateWebhook(false);
      setNewWebhook({ url: '', events: [] });
      loadWebhooks();
      toast.success('Webhook creado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear webhook');
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('¿Eliminar este webhook?')) return;
    try {
      await webhookService.delete(webhookId);
      loadWebhooks();
      toast.success('Webhook eliminado');
    } catch (error) {
      toast.error('Error al eliminar webhook');
    }
  };

  const handleToggleWebhook = async (webhook) => {
    try {
      await webhookService.update(webhook.id, { is_active: !webhook.is_active });
      loadWebhooks();
    } catch (error) {
      toast.error('Error al actualizar webhook');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const loadSettings = async () => {
    try {
      const response = await companyService.getSettings();
      const data = response.data;
      data.refinancing_policy_urls = data.refinancing_policy_urls || [];
      if (data.refinancing_policy_url && !data.refinancing_policy_urls.includes(data.refinancing_policy_url)) {
        data.refinancing_policy_urls.push(data.refinancing_policy_url);
      }
      setSettings(data);
      setBankAccounts(data.bank_accounts || []);
      if (data.mercadopago_credentials) {
        setMpCredentials({
          access_token: data.mercadopago_credentials.access_token || '',
          public_key: data.mercadopago_credentials.public_key || ''
        });
      }
      if (data.moracero_config) {
        setCrmConfig({
          url: data.moracero_config.url || '',
          client_id: data.moracero_config.client_id || '',
          username: data.moracero_config.username || '',
          password: data.moracero_config.password || '',
        });
      }
      // Load descargo config
      try {
        const descargoRes = await descargoService.getConfig();
        setDescargoConfig(descargoRes.data);
      } catch (e) { /* first time - use defaults */ }
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const loadBankAccounts = async () => {
    try {
      const response = await bankAccountService.list();
      setBankAccounts(response.data);
    } catch (error) {
      console.error('Error loading bank accounts:', error);
    }
  };

  const handleCreateBankAccount = async () => {
    if (!newBankAccount.bank.trim() || !newBankAccount.cbu.trim() || !newBankAccount.holder.trim()) {
      toast.error('Banco, CBU y Titular son obligatorios');
      return;
    }
    try {
      await bankAccountService.create(newBankAccount);
      toast.success('Cuenta bancaria agregada');
      setShowAddBankAccount(false);
      setNewBankAccount({ bank: '', account_type: 'caja_ahorro', cbu: '', alias: '', holder: '', is_default: false });
      loadBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar cuenta');
    }
  };

  const handleUpdateBankAccount = async () => {
    if (!editingBankAccount) return;
    try {
      await bankAccountService.update(editingBankAccount.id, editingBankAccount);
      toast.success('Cuenta actualizada');
      setEditingBankAccount(null);
      loadBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar cuenta');
    }
  };

  const handleDeleteBankAccount = async (accountId) => {
    if (!window.confirm('¿Eliminar esta cuenta bancaria?')) return;
    try {
      await bankAccountService.delete(accountId);
      toast.success('Cuenta eliminada');
      loadBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar cuenta');
    }
  };

  const checkMPConnection = async () => {
    const tokenToTest = mpCredentials.access_token || settings.mercadopago_credentials?.access_token;
    if (!tokenToTest) {
      setMpConnectionStatus('disconnected');
      return;
    }
    setMpConnectionStatus('checking');
    try {
      const response = await api.post('/company/mercadopago/test', {
        access_token: tokenToTest,
        public_key: mpCredentials.public_key || ''
      });
      setMpConnectionStatus(response.data.connected ? 'connected' : 'disconnected');
      if (response.data.connected) {
        toast.success(`Conexion exitosa${response.data.account_email ? ` - ${response.data.account_email}` : ''}`);
      } else {
        toast.error(response.data.message || 'No se pudo conectar');
      }
    } catch (error) {
      setMpConnectionStatus('disconnected');
      toast.error('Error al probar la conexion');
    }
  };

  const handleSaveMPCredentials = async () => {
    try {
      await api.put('/company/mercadopago-config', mpCredentials);
      await companyService.updateSettings({ mp_webhook_mode: settings.mp_webhook_mode });
      toast.success('Credenciales de Mercado Pago guardadas');
      loadSettings();
      // Verify connection with the just-saved credentials
      setMpConnectionStatus('checking');
      try {
        const response = await api.post('/company/mercadopago/test', mpCredentials);
        setMpConnectionStatus(response.data.connected ? 'connected' : 'disconnected');
        if (response.data.connected) {
          toast.success(`Conexion verificada${response.data.account_email ? ` - ${response.data.account_email}` : ''}`);
        }
      } catch { setMpConnectionStatus('disconnected'); }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar credenciales');
    }
  };

  const handleSave = async () => {
    try {
      await companyService.updateSettings(settings);
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const handleFieldVisibilityChange = (fieldId, isVisible) => {
    const fields = settings.debtor_fields_config?.fields || [];
    const updatedFields = fields.map(f => 
      f.field_id === fieldId ? { ...f, is_visible_to_debtor: isVisible } : f
    );
    setSettings({ ...settings, debtor_fields_config: { fields: updatedFields } });
  };

  const handleFieldOrderChange = (fieldId, newOrder) => {
    const fields = settings.debtor_fields_config?.fields || [];
    const updatedFields = fields.map(f => 
      f.field_id === fieldId ? { ...f, display_order: parseInt(newOrder) || 0 } : f
    );
    updatedFields.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    setSettings({ ...settings, debtor_fields_config: { fields: updatedFields } });
  };

  const handleAddCustomField = async () => {
    if (!newField.field_id || !newField.field_name) {
      toast.error('ID y Nombre del campo son obligatorios');
      return;
    }
    
    const fields = settings.debtor_fields_config?.fields || [];
    if (fields.some(f => f.field_id === newField.field_id)) {
      toast.error('Ya existe un campo con ese ID');
      return;
    }
    
    try {
      await companyService.addDebtorField({
        ...newField,
        is_standard: false,
        options: newField.field_type === 'select' ? newField.options : null
      });
      
      toast.success('Campo personalizado agregado');
      setShowAddField(false);
      setNewField({
        field_id: '',
        field_name: '',
        field_type: 'text',
        is_visible_to_debtor: true,
        is_required: false,
        display_order: 99,
        options: []
      });
      loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar campo');
    }
  };

  const handleDeleteCustomField = async (fieldId) => {
    if (!window.confirm('¿Eliminar este campo personalizado?')) return;
    
    try {
      await companyService.deleteDebtorField(fieldId);
      toast.success('Campo eliminado');
      loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar campo');
    }
  };

  const handleSaveFieldsConfig = async () => {
    try {
      const fields = settings.debtor_fields_config?.fields || [];
      await companyService.updateDebtorFields({ fields });
      toast.success('Configuración de campos guardada');
    } catch (error) {
      toast.error('Error al guardar configuración de campos');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
      toast.error('Solo se permiten archivos JPG, PNG o WEBP');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('El archivo no debe superar 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/company/upload-logo', formData);
      setSettings({ ...settings, logo_url: response.data.url });
      toast.success('Logo subido exitosamente');
    } catch (error) {
      toast.error('Error al subir el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const addPolicyUrl = () => {
    if (!newPolicyUrl.trim()) return;
    if (!newPolicyUrl.startsWith('http')) {
      toast.error('La URL debe comenzar con http:// o https://');
      return;
    }
    if (settings.refinancing_policy_urls.includes(newPolicyUrl)) {
      toast.error('Esta URL ya está agregada');
      return;
    }
    setSettings({
      ...settings,
      refinancing_policy_urls: [...settings.refinancing_policy_urls, newPolicyUrl]
    });
    setNewPolicyUrl('');
  };

  const removePolicyUrl = (url) => {
    setSettings({
      ...settings,
      refinancing_policy_urls: settings.refinancing_policy_urls.filter(u => u !== url)
    });
  };

  // CRM Moracero functions
  const saveCrmConfig = async () => {
    try {
      await api.put('/company/settings', {
        moracero_url: crmConfig.url,
        moracero_client_id: crmConfig.client_id,
        moracero_username: crmConfig.username,
        moracero_password: crmConfig.password,
      });
      toast.success('Configuración CRM guardada');
    } catch (error) {
      toast.error('Error al guardar configuración CRM');
    }
  };

  const testCrmConnection = async () => {
    setCrmTestStatus('testing');
    try {
      await api.post('/company/test-crm');
      setCrmTestStatus('success');
      toast.success('Conexión exitosa con Moracero');
    } catch (error) {
      setCrmTestStatus('error');
      toast.error(error?.response?.data?.detail || 'Error de conexión');
    }
  };

  const queryCrmByDni = async () => {
    if (!crmTestDni.trim()) return;
    setCrmQuerying(true);
    setCrmQueryResult(null);
    try {
      const response = await api.post('/company/query-crm', { dni: crmTestDni });
      setCrmQueryResult(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al consultar CRM');
    } finally {
      setCrmQuerying(false);
    }
  };

  const handlePolicyFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/^application\/pdf$/)) {
      toast.error('Solo se permiten archivos PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo no debe superar 10MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/company/upload-policy', formData);
      setSettings({
        ...settings,
        refinancing_policy_urls: [...settings.refinancing_policy_urls, response.data.url]
      });
      toast.success('Documento subido exitosamente');
    } catch (error) {
      toast.error('Error al subir el documento');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-teal-500" />
                </div>
                <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                  Configuración
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Sidebar Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="company-settings">
        <div className="flex gap-8">
          {/* Left Sidebar - Vertical Tabs */}
          <div className="w-64 flex-shrink-0">
            <Card className="p-2 sticky top-8">
              <nav className="space-y-1">
                {MENU_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                        activeSection === item.id
                          ? 'bg-teal-50 text-teal-700 font-medium'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                      data-testid={`menu-${item.id}`}
                    >
                      <Icon className={`w-5 h-5 ${activeSection === item.id ? 'text-teal-600' : 'text-slate-400'}`} />
                      {item.label}
                    </button>
                  );
                })}
              </nav>
            </Card>
            <p className="text-[10px] text-slate-400 text-center mt-3" data-testid="settings-version">MacroPago v{APP_VERSION}</p>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 min-w-0">
            {/* Apariencia Section */}
            {activeSection === 'appearance' && (
              <SettingsAppearance settings={settings} setSettings={setSettings} handleSave={handleSave} logoInputRef={logoInputRef} handleLogoUpload={handleLogoUpload} uploadingLogo={uploadingLogo} />
            )}

            {/* Acceso Section */}
            {activeSection === 'access' && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                  Configuración de Acceso
                </h3>
                
                <div className="space-y-6">
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-3">Métodos de Búsqueda</h4>
                    <p className="text-sm text-slate-600 mb-4">Selecciona cómo pueden los clientes buscar sus deudas</p>
                    
                    <div className="space-y-3">
                      {[
                        { key: 'search_by_dni', label: 'Buscar por DNI', desc: 'Documento Nacional de Identidad (8 dígitos)' },
                        { key: 'search_by_cuit', label: 'Buscar por CUIT', desc: 'Clave Única de Identificación Tributaria (11 dígitos)' },
                        { key: 'search_by_code', label: 'Buscar por Código de Cliente', desc: 'Código interno asignado al cliente' },
                        { key: 'search_by_plate', label: 'Buscar por Dominio', desc: 'Dominio de vehículo (para cobranza de multas)', special: true },
                      ].map(({ key, label, desc, special }) => (
                        <div key={key} className={`flex items-center justify-between p-3 bg-white rounded-lg border ${special ? 'border-amber-200 bg-amber-50' : ''}`}>
                          <div>
                            <p className="font-medium text-slate-900">{label}</p>
                            <p className="text-sm text-slate-600">{desc}</p>
                          </div>
                          <Switch
                            checked={settings[key]}
                            onCheckedChange={(checked) => setSettings({...settings, [key]: checked})}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-3">Verificación por Teléfono</h4>
                    
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium text-blue-900">Requerir verificación SMS</p>
                        <p className="text-sm text-blue-700">Envía un código de verificación por WhatsApp usando UltraMsg</p>
                      </div>
                      <Switch
                        checked={settings.require_phone_verification}
                        onCheckedChange={(checked) => setSettings({...settings, require_phone_verification: checked})}
                      />
                    </div>

                    {settings.require_phone_verification && (
                      <div className="bg-white rounded p-3 mt-2">
                        <p className="text-xs text-blue-800">
                          Asegúrate de configurar UltraMsg en el panel de SuperAdmin para que funcione.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-900 mb-3">Burbuja de WhatsApp</h4>
                    <p className="text-sm text-green-700 mb-3">Muestra un boton flotante de WhatsApp en el portal del cliente</p>

                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium text-green-900">Habilitar burbuja</p>
                        <p className="text-sm text-green-700">Se muestra en busqueda y ficha del cliente</p>
                      </div>
                      <Switch
                        checked={settings.whatsapp_bubble_enabled || false}
                        onCheckedChange={(checked) => setSettings({...settings, whatsapp_bubble_enabled: checked})}
                        data-testid="whatsapp-bubble-switch"
                      />
                    </div>

                    {settings.whatsapp_bubble_enabled && (
                      <div className="space-y-3 bg-white rounded-lg p-3">
                        <div>
                          <Label className="text-sm">Numero de WhatsApp (con codigo de pais)</Label>
                          <Input
                            placeholder="5493814001234"
                            value={settings.whatsapp_bubble_number || ''}
                            onChange={(e) => setSettings({...settings, whatsapp_bubble_number: e.target.value})}
                            data-testid="whatsapp-bubble-number"
                          />
                          <p className="text-xs text-slate-500 mt-1">Sin +, sin espacios. Ej: 5493814001234</p>
                        </div>
                        <div>
                          <Label className="text-sm">Mensaje predeterminado (opcional)</Label>
                          <Input
                            placeholder="Hola, necesito ayuda con mi deuda"
                            value={settings.whatsapp_bubble_message || ''}
                            onChange={(e) => setSettings({...settings, whatsapp_bubble_message: e.target.value})}
                            data-testid="whatsapp-bubble-message"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <Button onClick={handleSave} className="w-full bg-teal-600 hover:bg-teal-700">
                    Guardar Configuración de Acceso
                  </Button>
                </div>
              </Card>
            )}

            {/* Pagos Section - ENHANCED */}
            {activeSection === 'payments' && (
              <SettingsPayments settings={settings} setSettings={setSettings} handleSave={handleSave} mpConnectionStatus={mpConnectionStatus} setShowMPConfig={setShowMPConfig} bankAccounts={bankAccounts} setShowAddBankAccount={setShowAddBankAccount} setEditingBankAccount={setEditingBankAccount} handleDeleteBankAccount={handleDeleteBankAccount} />
            )}

            {/* Campos de Deudor Section */}
            {activeSection === 'fields' && (
              <Card className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>
                      Campos de Cliente
                    </h3>
                    <p className="text-sm text-slate-600">
                      Configura qué campos son visibles al cliente y su orden de aparición
                    </p>
                  </div>
                  <Button onClick={() => setShowAddField(true)} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Campo
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {(settings.debtor_fields_config?.fields || []).map((field, idx) => (
                    <div 
                      key={field.field_id} 
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        field.is_standard ? 'bg-slate-50 border-slate-200' : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-slate-400">
                          <GripVertical className="w-4 h-4" />
                          <Input
                            type="number"
                            value={field.display_order || idx}
                            onChange={(e) => handleFieldOrderChange(field.field_id, e.target.value)}
                            className="w-16 h-8 text-center"
                            min="1"
                          />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{field.field_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {FIELD_TYPES.find(t => t.value === field.field_type)?.label || field.field_type}
                            </Badge>
                            {field.is_standard && (
                              <Badge className="text-xs bg-slate-200 text-slate-700">Estándar</Badge>
                            )}
                            {!field.is_standard && (
                              <Badge className="text-xs bg-blue-200 text-blue-700">Personalizado</Badge>
                            )}
                            <span className="text-xs text-slate-500">ID: {field.field_id}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">Visible al cliente</span>
                          <button
                            onClick={() => handleFieldVisibilityChange(field.field_id, !field.is_visible_to_debtor)}
                            className={`p-2 rounded ${
                              field.is_visible_to_debtor 
                                ? 'bg-green-100 text-green-600' 
                                : 'bg-slate-100 text-slate-400'
                            }`}
                          >
                            {field.is_visible_to_debtor ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                        </div>
                        
                        {!field.is_standard && (
                          <button
                            onClick={() => handleDeleteCustomField(field.field_id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <Button onClick={handleSaveFieldsConfig} className="w-full mt-6 bg-teal-600 hover:bg-teal-700">
                  Guardar Configuración de Campos
                </Button>
              </Card>
            )}

            {/* Negociación Section */}
            {activeSection === 'negotiation' && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                  Estrategias de Negociación
                </h3>
                
                <div className="space-y-6">
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-teal-900">Mostrar Usuarios en Línea</p>
                        <p className="text-sm text-teal-700">Muestra un contador ficticio de personas consultando (aumenta urgencia)</p>
                      </div>
                      <Switch
                        checked={settings.show_online_users}
                        onCheckedChange={(checked) => setSettings({...settings, show_online_users: checked})}
                      />
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-3">Campos a Mostrar al Cliente</h4>
                    <p className="text-sm text-slate-600 mb-4">
                      Selecciona qué información verá el cliente en su portal
                    </p>
                    
                    <div className="space-y-2">
                      {[
                        { id: 'total_debt', label: 'Deuda Total' },
                        { id: 'due_date', label: 'Fecha de Vencimiento' },
                        { id: 'invoice_count', label: 'Cantidad de Facturas' },
                        { id: 'payment_history', label: 'Historial de Pagos' },
                        { id: 'interest_rate', label: 'Tasa de Interés' }
                      ].map(({ id, label }) => (
                        <label key={id} className="flex items-center gap-3 p-3 bg-white rounded border hover:border-teal-500 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.negotiation_fields?.includes(id)}
                            onChange={(e) => {
                              const fields = e.target.checked
                                ? [...(settings.negotiation_fields || []), id]
                                : settings.negotiation_fields.filter(f => f !== id);
                              setSettings({...settings, negotiation_fields: fields});
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-sm text-slate-700">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <Button onClick={handleSave} className="w-full bg-teal-600 hover:bg-teal-700">
                    Guardar Configuración de Negociación
                  </Button>
                </div>
              </Card>
            )}

            {/* Refinanciación Section */}
            {activeSection === 'refinancing' && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                  Políticas de Refinanciación
                </h3>
                
                <div className="space-y-6">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-amber-900">Habilitar Refinanciación</p>
                        <p className="text-sm text-amber-700">Permite ofrecer planes de pago y refinanciamiento</p>
                      </div>
                      <Switch
                        checked={settings.enable_refinancing}
                        onCheckedChange={(checked) => setSettings({...settings, enable_refinancing: checked})}
                      />
                    </div>
                  </div>

                  {settings.enable_refinancing && (
                    <>
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                        <h4 className="font-medium text-slate-900 mb-3">Documentos de Políticas</h4>
                        <p className="text-sm text-slate-600 mb-4">
                          Agrega URLs o sube archivos PDF con las políticas de refinanciación
                        </p>
                        
                        <div className="flex gap-2 mb-3">
                          <Input
                            value={newPolicyUrl}
                            onChange={(e) => setNewPolicyUrl(e.target.value)}
                            placeholder="https://ejemplo.com/politicas.pdf"
                            onKeyDown={(e) => e.key === 'Enter' && addPolicyUrl()}
                          />
                          <Button type="button" variant="outline" onClick={addPolicyUrl}>
                            <Link className="w-4 h-4 mr-1" />
                            Agregar URL
                          </Button>
                        </div>

                        <div className="flex gap-2 mb-4">
                          <input
                            type="file"
                            ref={policyFileRef}
                            onChange={handlePolicyFileUpload}
                            accept="application/pdf"
                            className="hidden"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => policyFileRef.current?.click()}
                            className="w-full"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Subir Archivo PDF
                          </Button>
                        </div>

                        {settings.refinancing_policy_urls.length > 0 && (
                          <div className="space-y-2">
                            <Label className="text-sm">Documentos agregados:</Label>
                            {settings.refinancing_policy_urls.map((url) => (
                              <div key={url} className="flex items-center justify-between p-2 bg-white rounded border">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  {url.endsWith('.pdf') ? (
                                    <File className="w-4 h-4 text-red-500 flex-shrink-0" />
                                  ) : (
                                    <Link className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                  )}
                                  <a 
                                    href={url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:underline truncate"
                                  >
                                    {url}
                                  </a>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removePolicyUrl(url)}
                                  className="text-red-600 hover:bg-red-50 flex-shrink-0"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <Button onClick={handleSave} className="w-full bg-teal-600 hover:bg-teal-700">
                    Guardar Políticas de Refinanciación
                  </Button>
                </div>
              </Card>
            )}

            {/* API & Webhooks Section */}
            {activeSection === 'api' && (
              <SettingsApiWebhooks apiKeys={apiKeys} webhooks={webhooks} setShowCreateApiKey={setShowCreateApiKey} setShowCreateWebhook={setShowCreateWebhook} handleRevokeApiKey={handleRevokeApiKey} handleEditApiKey={handleEditApiKey} handleDeleteWebhook={handleDeleteWebhook} handleToggleWebhook={handleToggleWebhook} />
            )}

            {activeSection === 'gestion' && <SettingsGestion />}

            {activeSection === 'crm' && (
              <SettingsCrm crmConfig={crmConfig} setCrmConfig={setCrmConfig} crmTestStatus={crmTestStatus} testCrmConnection={testCrmConnection} saveCrmConfig={saveCrmConfig} crmTestDni={crmTestDni} setCrmTestDni={setCrmTestDni} crmQuerying={crmQuerying} queryCrmByDni={queryCrmByDni} crmQueryResult={crmQueryResult} />
            )}

            {activeSection === 'email' && (
              <SettingsEmail settings={settings} setSettings={setSettings} handleSave={handleSave} />
            )}

            {/* Descargos Config Section */}
            {activeSection === 'descargos' && (
              <SettingsDescargos descargoConfig={descargoConfig} setDescargoConfig={setDescargoConfig} newCause={newCause} setNewCause={setNewCause} />
            )}

            {/* Widget Web Section */}
            {activeSection === 'widget' && (
              <SettingsWidget settings={settings} />
            )}
          </div>
        </div>
      </div>

      {/* Create / Edit API Key Dialog */}
      <Dialog open={showCreateApiKey} onOpenChange={(open) => { setShowCreateApiKey(open); if (!open) { setEditingApiKey(null); setNewApiKey({ name: '', permissions: ['debtors:read', 'payments:read'], default_portfolio_id: '' }); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="api-key-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              {editingApiKey ? 'Editar API Key' : 'Crear API Key'}
            </DialogTitle>
            <DialogDescription>
              {editingApiKey ? <>Modificá nombre, cartera por defecto o permisos. La clave <span className="font-mono">{editingApiKey.key_prefix}...</span> no cambia.</> : 'Genera una nueva clave para acceder a la API'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={newApiKey.name}
                onChange={(e) => setNewApiKey({...newApiKey, name: e.target.value})}
                placeholder="Integración CRM, Automatización, etc."
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Cartera por defecto (para actas por API)</Label>
              <Select value={newApiKey.default_portfolio_id || 'none'} onValueChange={(v) => setNewApiKey({...newApiKey, default_portfolio_id: v === 'none' ? '' : v})}>
                <SelectTrigger className="mt-1" data-testid="api-key-default-portfolio">
                  <SelectValue placeholder="Sin cartera por defecto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin cartera por defecto</SelectItem>
                  {apiPortfolios.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Las actas enviadas por <code>POST /api/v1/actas</code> sin <code>portfolio_id</code> se cargan en esta cartera.</p>
            </div>

            <div>
              <Label>Permisos</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {API_PERMISSIONS.map((perm) => (
                  <label key={perm.id} className="flex items-center gap-2 p-2 rounded border hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newApiKey.permissions.includes(perm.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewApiKey({...newApiKey, permissions: [...newApiKey.permissions, perm.id]});
                        } else {
                          setNewApiKey({...newApiKey, permissions: newApiKey.permissions.filter(p => p !== perm.id)});
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{perm.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {editingApiKey && (
              <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 cursor-pointer">
                <div>
                  <p className="text-sm font-medium text-slate-900">Clave activa</p>
                  <p className="text-xs text-slate-500">Desactivala para bloquear el acceso sin borrarla</p>
                </div>
                <Switch checked={!!newApiKey.is_active} onCheckedChange={(v) => setNewApiKey({ ...newApiKey, is_active: v })} data-testid="api-key-active-switch" />
              </label>
            )}

            <Button onClick={handleCreateApiKey} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="api-key-submit">
              {editingApiKey ? 'Guardar cambios' : 'Crear API Key'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* API Key Created Dialog */}
      <Dialog open={!!createdApiKey} onOpenChange={() => setCreatedApiKey(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="api-key-created-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              API Key Creada
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 font-medium mb-2">
                ⚠️ Guarda esta clave en un lugar seguro. No podrás verla de nuevo.
              </p>
            </div>
            
            <div>
              <Label>Tu API Key</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={createdApiKey?.secret_key || ''}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(createdApiKey?.secret_key)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-600 mb-2">Ejemplo de uso:</p>
              <pre className="bg-slate-900 text-emerald-400 p-3 rounded text-xs overflow-x-auto">
{`# Crear acta (crea el deudor si no existe)
curl -X POST "${window.location.origin}/api/v1/actas" \\
  -H "X-API-Key: ${createdApiKey?.secret_key || 'tu-api-key'}" \\
  -H "Content-Type: application/json" \\
  -d '{"plate":"AB123CD","acta_number":"T-129304","infraccion":"Cruce de semáforo en rojo","fecha":"2026-05-10","hora":"14:32","monto":350000,"monto_quita":210000,"discount_expiry":"2026-07-31","titular":{"name":"Juan Pérez","dni":"30111222","phone":"351 555 1234","email":"juan@mail.com","phones":["351 444 5555",{"value":"11 4444 5555","label":"Laboral"}],"emails":["juan.perez@trabajo.com"]}}'
# titular.phones / titular.emails: contactos adicionales; se suman a la ficha CRM sin borrar los existentes

# Carga masiva (hasta 500 actas por llamada)
curl -X POST "${window.location.origin}/api/v1/actas/batch" \\
  -H "X-API-Key: ${createdApiKey?.secret_key || 'tu-api-key'}" \\
  -H "Content-Type: application/json" \\
  -d '{"actas":[{"plate":"AB123CD","acta_number":"T-1","monto":350000,"titular":{"name":"Juan Pérez"}},{"plate":"AC456EF","acta_number":"T-2","monto":200000,"titular":{"name":"Ana Gómez"}}]}'

# Subir PDF y video del acta
curl -X POST "${window.location.origin}/api/v1/actas/T-129304/archivos" \\
  -H "X-API-Key: ${createdApiKey?.secret_key || 'tu-api-key'}" \\
  -F "pdf=@acta.pdf" -F "video=@infraccion.mp4"

# Consultar estado del acta
curl "${window.location.origin}/api/v1/actas/T-129304" \\
  -H "X-API-Key: ${createdApiKey?.secret_key || 'tu-api-key'}"`}
              </pre>
              <a href={`${window.location.origin}/api/docs`} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-700 hover:underline mt-2 inline-block" data-testid="api-docs-link">Ver documentación completa (Swagger) →</a>
            </div>

            <Button onClick={() => setCreatedApiKey(null)} className="w-full">
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Webhook Dialog */}
      <Dialog open={showCreateWebhook} onOpenChange={setShowCreateWebhook}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Webhook className="w-5 h-5 text-violet-600" />
              Crear Webhook
            </DialogTitle>
            <DialogDescription>
              Configura una URL para recibir notificaciones
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>URL del Webhook *</Label>
              <Input
                value={newWebhook.url}
                onChange={(e) => setNewWebhook({...newWebhook, url: e.target.value})}
                placeholder="https://tu-servidor.com/webhook"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Debe usar HTTPS</p>
            </div>
            
            <div>
              <Label>Eventos a escuchar</Label>
              <div className="space-y-2 mt-2">
                {webhookEvents.events?.map((event) => (
                  <label key={event} className="flex items-center gap-3 p-3 rounded border hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newWebhook.events.includes(event)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewWebhook({...newWebhook, events: [...newWebhook.events, event]});
                        } else {
                          setNewWebhook({...newWebhook, events: newWebhook.events.filter(ev => ev !== event)});
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <div>
                      <span className="text-sm font-medium">{event}</span>
                      <p className="text-xs text-slate-500">{webhookEvents.descriptions?.[event]}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <Button onClick={handleCreateWebhook} className="w-full bg-violet-600 hover:bg-violet-700">
              Crear Webhook
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mercado Pago Config Dialog */}
      <Dialog open={showMPConfig} onOpenChange={setShowMPConfig}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Configuración de Mercado Pago
            </DialogTitle>
            <DialogDescription>
              Ingresa tus credenciales de Mercado Pago para recibir pagos
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                Puedes obtener tus credenciales en <a href="https://www.mercadopago.com.ar/developers/panel/app" target="_blank" rel="noopener noreferrer" className="font-medium underline">Mercado Pago Developers</a>
              </p>
            </div>
            
            <div>
              <Label>Access Token *</Label>
              <Input
                type="password"
                value={mpCredentials.access_token}
                onChange={(e) => setMpCredentials({...mpCredentials, access_token: e.target.value})}
                placeholder="APP_USR-..."
                className="mt-1 font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">Token de acceso de producción</p>
            </div>
            
            <div>
              <Label>Public Key *</Label>
              <Input
                value={mpCredentials.public_key}
                onChange={(e) => setMpCredentials({...mpCredentials, public_key: e.target.value})}
                placeholder="APP_USR-..."
                className="mt-1 font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">Clave pública de producción</p>
            </div>

            {/* Webhook Configuration */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-800 mb-2">URL de Webhook para tu empresa</p>
              <p className="text-xs text-amber-700 mb-2">
                Configura esta URL en tu panel de Mercado Pago para recibir notificaciones automaticas de pagos:
              </p>
              <div className="flex items-center gap-2 bg-white rounded border p-2">
                <code className="text-xs flex-1 font-mono text-slate-700 truncate" data-testid="mp-webhook-url">
                  {process.env.REACT_APP_BACKEND_URL}/api/webhooks/mp/{settings.id}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/mp/${settings.id}`);
                    toast.success('URL de webhook copiada');
                  }}
                  className="h-7 px-2"
                  data-testid="copy-webhook-url"
                >
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <p className="text-xs text-amber-600 mt-2">
                <a href="https://www.mercadopago.com.ar/developers/panel/webhooks" target="_blank" rel="noopener noreferrer" className="underline">
                  Ir al panel de Webhooks de MP
                </a>
              </p>
            </div>

            {/* Webhook Mode */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-sm font-medium text-slate-800 mb-2">Modo de procesamiento de pagos</p>
              <div className="space-y-2">
                <label className="flex items-start gap-3 p-2 rounded-lg cursor-pointer hover:bg-white transition-colors">
                  <input
                    type="radio"
                    name="webhook_mode"
                    value="auto"
                    checked={settings.mp_webhook_mode === 'auto'}
                    onChange={() => setSettings({...settings, mp_webhook_mode: 'auto'})}
                    className="mt-1"
                    data-testid="webhook-mode-auto"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-900">Automatico</p>
                    <p className="text-xs text-slate-500">Al recibir un pago aprobado, se marca la deuda como pagada automaticamente y se registra el pago.</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-2 rounded-lg cursor-pointer hover:bg-white transition-colors">
                  <input
                    type="radio"
                    name="webhook_mode"
                    value="review"
                    checked={settings.mp_webhook_mode === 'review'}
                    onChange={() => setSettings({...settings, mp_webhook_mode: 'review'})}
                    className="mt-1"
                    data-testid="webhook-mode-review"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-900">Revision manual</p>
                    <p className="text-xs text-slate-500">Al recibir un pago, se crea una solicitud pendiente para que un admin la revise y apruebe antes de actualizar la deuda.</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Connection Status */}
            <div className={`p-3 rounded-lg border ${
              mpConnectionStatus === 'connected' ? 'bg-emerald-50 border-emerald-200' :
              mpConnectionStatus === 'disconnected' ? 'bg-red-50 border-red-200' :
              'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-center gap-2">
                {mpConnectionStatus === 'connected' && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                {mpConnectionStatus === 'disconnected' && <XCircle className="w-5 h-5 text-red-600" />}
                {mpConnectionStatus === 'checking' && <Loader2 className="w-5 h-5 text-slate-600 animate-spin" />}
                {mpConnectionStatus === 'unknown' && <Settings2 className="w-5 h-5 text-slate-600" />}
                <span className={`font-medium ${
                  mpConnectionStatus === 'connected' ? 'text-emerald-700' :
                  mpConnectionStatus === 'disconnected' ? 'text-red-700' :
                  'text-slate-700'
                }`}>
                  {mpConnectionStatus === 'connected' && 'Conexión verificada'}
                  {mpConnectionStatus === 'disconnected' && 'No conectado'}
                  {mpConnectionStatus === 'checking' && 'Verificando conexión...'}
                  {mpConnectionStatus === 'unknown' && 'Estado de conexión desconocido'}
                </span>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={checkMPConnection}
                disabled={!mpCredentials.access_token || mpConnectionStatus === 'checking'}
                className="flex-1"
              >
                {mpConnectionStatus === 'checking' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando...</>
                ) : (
                  'Probar Conexión'
                )}
              </Button>
              <Button
                onClick={handleSaveMPCredentials}
                disabled={!mpCredentials.access_token || !mpCredentials.public_key}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Guardar Credenciales
              </Button>
            </div>

            {/* Separator + Manual QR - only shown when API is NOT connected */}
            {mpConnectionStatus !== 'connected' && (
            <>
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
              <div className="relative flex justify-center"><span className="px-3 bg-white text-xs text-slate-500 font-medium">O sin integrar por API</span></div>
            </div>

            {/* Manual QR Option */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium text-slate-800">QR Manual de Mercado Pago</p>
              <p className="text-xs text-slate-500">Si no tienes token de API, puedes cargar una imagen de tu QR de Mercado Pago</p>
              <div>
                <Label>Imagen QR de Mercado Pago</Label>
                {settings.mp_manual_qr_url && (
                  <div className="flex items-center gap-3 mb-2 mt-1">
                    <img src={settings.mp_manual_qr_url} alt="QR MP" className="w-20 h-20 object-contain rounded-lg border" />
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setSettings({...settings, mp_manual_qr_url: ''})}>Quitar</Button>
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="cursor-pointer mt-1"
                  data-testid="mp-qr-upload-input"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await api.post('/company/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                      const imgUrl = res.data.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${res.data.url}` : res.data.url;
                      setSettings({...settings, mp_manual_qr_url: imgUrl});
                      toast.success('QR cargado');
                    } catch { toast.error('Error al subir imagen'); }
                  }}
                />
              </div>
              <div>
                <Label>Titular de la cuenta MP</Label>
                <Input
                  value={settings.mp_manual_holder || ''}
                  onChange={(e) => setSettings({...settings, mp_manual_holder: e.target.value})}
                  placeholder="Juan Pérez"
                  className="mt-1 text-sm"
                  data-testid="mp-manual-holder-input"
                />
              </div>
              <div>
                <Label>CUIT del titular</Label>
                <Input
                  value={settings.mp_manual_cuit || ''}
                  onChange={(e) => setSettings({...settings, mp_manual_cuit: e.target.value})}
                  placeholder="20-12345678-9"
                  className="mt-1 text-sm"
                  data-testid="mp-manual-cuit-input"
                />
              </div>
              <Button 
                onClick={handleSave}
                variant="outline"
                className="w-full"
                data-testid="save-mp-manual-btn"
              >
                Guardar QR Manual
              </Button>
            </div>
            </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Custom Field Dialog */}
      <Dialog open={showAddField} onOpenChange={setShowAddField}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Campo Personalizado</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>ID del Campo</Label>
              <Input
                value={newField.field_id}
                onChange={(e) => setNewField({...newField, field_id: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                placeholder="direccion, telefono_2, etc"
              />
              <p className="text-xs text-slate-500 mt-1">Identificador único (sin espacios)</p>
            </div>
            
            <div>
              <Label>Nombre del Campo</Label>
              <Input
                value={newField.field_name}
                onChange={(e) => setNewField({...newField, field_name: e.target.value})}
                placeholder="Dirección, Teléfono Alternativo, etc"
              />
            </div>
            
            <div>
              <Label>Tipo de Campo</Label>
              <Select
                value={newField.field_type}
                onValueChange={(value) => setNewField({...newField, field_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {newField.field_type === 'select' && (
              <div>
                <Label>Opciones</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newFieldOption}
                    onChange={(e) => setNewFieldOption(e.target.value)}
                    placeholder="Agregar opción"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newFieldOption) {
                        setNewField({
                          ...newField, 
                          options: [...newField.options, newFieldOption]
                        });
                        setNewFieldOption('');
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (newFieldOption) {
                        setNewField({
                          ...newField, 
                          options: [...newField.options, newFieldOption]
                        });
                        setNewFieldOption('');
                      }
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {newField.options.map((opt) => (
                    <Badge key={opt} variant="secondary" className="cursor-pointer" onClick={() => {
                      setNewField({
                        ...newField,
                        options: newField.options.filter((o) => o !== opt)
                      });
                    }}>
                      {opt} <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <Label>Visible al cliente en autoconsulta</Label>
              <Switch
                checked={newField.is_visible_to_debtor}
                onCheckedChange={(checked) => setNewField({...newField, is_visible_to_debtor: checked})}
              />
            </div>
            
            <div>
              <Label>Orden de Visualización</Label>
              <Input
                type="number"
                value={newField.display_order}
                onChange={(e) => setNewField({...newField, display_order: parseInt(e.target.value) || 0})}
                min="1"
              />
            </div>
            
            <Button onClick={handleAddCustomField} className="w-full bg-teal-600 hover:bg-teal-700">
              Agregar Campo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Bank Account Dialog */}
      <Dialog open={showAddBankAccount} onOpenChange={setShowAddBankAccount}>
        <DialogContent data-testid="add-bank-account-dialog">
          <DialogHeader>
            <DialogTitle>Agregar Cuenta Bancaria</DialogTitle>
            <DialogDescription>
              Agrega una cuenta bancaria para recibir transferencias
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Banco *</Label>
              <Input
                value={newBankAccount.bank}
                onChange={(e) => setNewBankAccount({...newBankAccount, bank: e.target.value})}
                placeholder="Ej: Banco Santander"
                data-testid="bank-name-input"
              />
            </div>
            <div>
              <Label>Tipo de Cuenta</Label>
              <Select
                value={newBankAccount.account_type}
                onValueChange={(v) => setNewBankAccount({...newBankAccount, account_type: v})}
              >
                <SelectTrigger data-testid="account-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="caja_ahorro">Caja de Ahorro</SelectItem>
                  <SelectItem value="cuenta_corriente">Cuenta Corriente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>CBU * (22 dígitos)</Label>
              <Input
                value={newBankAccount.cbu}
                onChange={(e) => setNewBankAccount({...newBankAccount, cbu: e.target.value})}
                placeholder="0000000000000000000000"
                maxLength={22}
                data-testid="cbu-input"
              />
            </div>
            <div>
              <Label>Alias (opcional)</Label>
              <Input
                value={newBankAccount.alias}
                onChange={(e) => setNewBankAccount({...newBankAccount, alias: e.target.value})}
                placeholder="mi.alias.mp"
                data-testid="alias-input"
              />
            </div>
            <div>
              <Label>Titular de la Cuenta *</Label>
              <Input
                value={newBankAccount.holder}
                onChange={(e) => setNewBankAccount({...newBankAccount, holder: e.target.value})}
                placeholder="Nombre y Apellido"
                data-testid="holder-input"
              />
            </div>
            <div>
              <Label>Logo del Banco</Label>
              {newBankAccount.logo_url && (
                <div className="flex items-center gap-2 mb-2">
                  <img src={newBankAccount.logo_url} alt="Logo" className="w-8 h-8 object-contain rounded border" />
                  <Button variant="ghost" size="sm" className="text-red-600 h-7 px-2" onClick={() => setNewBankAccount({...newBankAccount, logo_url: ''})}>Quitar</Button>
                </div>
              )}
              <Input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="cursor-pointer"
                data-testid="bank-logo-input"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const formData = new FormData();
                    formData.append('file', file);
                    const res = await api.post('/company/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                    const imgUrl = res.data.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${res.data.url}` : res.data.url;
                    setNewBankAccount({...newBankAccount, logo_url: imgUrl});
                    toast.success('Logo cargado');
                  } catch { toast.error('Error al subir imagen'); }
                }}
              />
              <p className="text-xs text-slate-500 mt-1">JPG, PNG o WEBP (máx. 2MB)</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={newBankAccount.is_default}
                onCheckedChange={(v) => setNewBankAccount({...newBankAccount, is_default: v})}
                data-testid="is-default-switch"
              />
              <Label>Establecer como cuenta principal</Label>
            </div>
            <Button 
              onClick={handleCreateBankAccount} 
              className="w-full bg-teal-600 hover:bg-teal-700"
              data-testid="save-bank-account-btn"
            >
              Agregar Cuenta
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Bank Account Dialog */}
      <Dialog open={!!editingBankAccount} onOpenChange={(open) => !open && setEditingBankAccount(null)}>
        <DialogContent data-testid="edit-bank-account-dialog">
          <DialogHeader>
            <DialogTitle>Editar Cuenta Bancaria</DialogTitle>
          </DialogHeader>
          {editingBankAccount && (
            <div className="space-y-4">
              <div>
                <Label>Banco *</Label>
                <Input
                  value={editingBankAccount.bank}
                  onChange={(e) => setEditingBankAccount({...editingBankAccount, bank: e.target.value})}
                />
              </div>
              <div>
                <Label>Tipo de Cuenta</Label>
                <Select
                  value={editingBankAccount.account_type}
                  onValueChange={(v) => setEditingBankAccount({...editingBankAccount, account_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="caja_ahorro">Caja de Ahorro</SelectItem>
                    <SelectItem value="cuenta_corriente">Cuenta Corriente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>CBU * (22 dígitos)</Label>
                <Input
                  value={editingBankAccount.cbu}
                  onChange={(e) => setEditingBankAccount({...editingBankAccount, cbu: e.target.value})}
                  maxLength={22}
                />
              </div>
              <div>
                <Label>Alias (opcional)</Label>
                <Input
                  value={editingBankAccount.alias || ''}
                  onChange={(e) => setEditingBankAccount({...editingBankAccount, alias: e.target.value})}
                />
              </div>
              <div>
                <Label>Titular de la Cuenta *</Label>
                <Input
                  value={editingBankAccount.holder}
                  onChange={(e) => setEditingBankAccount({...editingBankAccount, holder: e.target.value})}
                />
              </div>
              <div>
                <Label>Logo del Banco</Label>
                {editingBankAccount.logo_url && (
                  <div className="flex items-center gap-2 mb-2">
                    <img src={editingBankAccount.logo_url} alt="Logo" className="w-8 h-8 object-contain rounded border" />
                    <Button variant="ghost" size="sm" className="text-red-600 h-7 px-2" onClick={() => setEditingBankAccount({...editingBankAccount, logo_url: ''})}>Quitar</Button>
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="cursor-pointer"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await api.post('/company/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                      const imgUrl = res.data.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${res.data.url}` : res.data.url;
                      setEditingBankAccount({...editingBankAccount, logo_url: imgUrl});
                      toast.success('Logo cargado');
                    } catch { toast.error('Error al subir imagen'); }
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingBankAccount.is_default}
                  onCheckedChange={(v) => setEditingBankAccount({...editingBankAccount, is_default: v})}
                />
                <Label>Cuenta principal</Label>
              </div>
              <Button 
                onClick={handleUpdateBankAccount} 
                className="w-full bg-teal-600 hover:bg-teal-700"
              >
                Guardar Cambios
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanySettings;
